// src/pages/AppliAdmin/ContactForm/useContactFormMessages.js
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllMessagesContactForm,
  getUnreadMessagesCountContactForm,
  markMessageContactForm,
  markAllMessageContactForm,
  deleteMessageContactForm,
} from "./../../services/contactformServices";

import {
  buildContactMessagesCSV,
  downloadCSV,
  isToday,
  toContactDate,
  toDateOnly,
} from "./contactFormUtils";

export function useContactFormMessages() {
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [markingId, setMarkingId] = useState(null);

  const [q, setQ] = useState("");
  const [readFilter, setReadFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState("date_desc");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const count = await getUnreadMessagesCountContactForm();
      setUnreadCount(Number(count || 0));
    } catch (error) {
      console.error("Erreur compteur messages non lus :", error);
    }
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAllMessagesContactForm();
      setMessages(Array.isArray(data) ? data : []);

      await refreshUnreadCount();
    } catch (error) {
      console.error("Erreur chargement messages contact :", error);
      toast.error("Erreur lors du chargement des messages.");
    } finally {
      setLoading(false);
    }
  }, [refreshUnreadCount]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const filteredMessages = useMemo(() => {
    let nextMessages = [...messages];

    const search = q.trim().toLowerCase();

    if (search) {
      nextMessages = nextMessages.filter((message) => {
        const searchable = [
          message.name,
          message.email,
          message.message,
          message.id,
        ]
          .map((item) => String(item || "").toLowerCase())
          .join(" ");

        return searchable.includes(search);
      });
    }

    if (readFilter !== "all") {
      nextMessages = nextMessages.filter((message) =>
        readFilter === "unread"
          ? !message.read_message
          : Boolean(message.read_message),
      );
    }

    if (dateFrom) {
      const from = new Date(`${dateFrom}T00:00:00`);
      nextMessages = nextMessages.filter((message) => {
        const date = toDateOnly(message.created_at);
        return date && date >= from;
      });
    }

    if (dateTo) {
      const to = new Date(`${dateTo}T23:59:59`);
      nextMessages = nextMessages.filter((message) => {
        const date = toContactDate(message.created_at);
        return date && date <= to;
      });
    }

    nextMessages.sort((a, b) => {
      if (sort === "date_desc") {
        return (
          (toContactDate(b.created_at)?.getTime() || 0) -
          (toContactDate(a.created_at)?.getTime() || 0)
        );
      }

      if (sort === "date_asc") {
        return (
          (toContactDate(a.created_at)?.getTime() || 0) -
          (toContactDate(b.created_at)?.getTime() || 0)
        );
      }

      if (sort === "name_az") {
        return String(a.name || "").localeCompare(String(b.name || ""), "fr");
      }

      if (sort === "name_za") {
        return String(b.name || "").localeCompare(String(a.name || ""), "fr");
      }

      return 0;
    });

    return nextMessages;
  }, [messages, q, readFilter, dateFrom, dateTo, sort]);

  const pageCount = Math.max(1, Math.ceil(filteredMessages.length / pageSize));

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredMessages.slice(start, start + pageSize);
  }, [filteredMessages, page, pageSize]);

  const stats = useMemo(() => {
    const total = messages.length;
    const filtered = filteredMessages.length;
    const unread = messages.filter((message) => !message.read_message).length;
    const read = total - unread;
    const today = messages.filter((message) =>
      isToday(message.created_at),
    ).length;

    const lastMessage = [...messages].sort(
      (a, b) =>
        (toContactDate(b.created_at)?.getTime() || 0) -
        (toContactDate(a.created_at)?.getTime() || 0),
    )[0];

    return {
      total,
      filtered,
      unread,
      read,
      today,
      lastMessage,
    };
  }, [messages, filteredMessages]);

  const toggleReadStatus = useCallback(
    async (message) => {
      if (!message?.id) return;

      const next = !Boolean(message.read_message);
      const previousMessages = messages;

      try {
        setMarkingId(message.id);

        setMessages((prev) =>
          prev.map((item) =>
            item.id === message.id ? { ...item, read_message: next } : item,
          ),
        );

        await markMessageContactForm(message.id, next);

        toast.success(
          next ? "Message marqué comme lu." : "Message marqué comme non lu.",
        );

        await refreshUnreadCount();
      } catch (error) {
        console.error("Erreur changement statut message :", error);
        setMessages(previousMessages);
        toast.error("Impossible de modifier le statut du message.");
      } finally {
        setMarkingId(null);
      }
    },
    [messages, refreshUnreadCount],
  );

  const markAllAsRead = useCallback(async () => {
    try {
      await markAllMessageContactForm();

      setMessages((prev) =>
        prev.map((message) => ({
          ...message,
          read_message: true,
        })),
      );

      setUnreadCount(0);
      toast.success("Tous les messages ont été marqués comme lus.");
    } catch (error) {
      console.error("Erreur marquage tous les messages :", error);
      toast.error("Impossible de marquer tous les messages comme lus.");
    }
  }, []);

  const deleteMessage = useCallback(
    async (messageId) => {
      if (!messageId) return;

      const confirmed = window.confirm("Supprimer définitivement ce message ?");
      if (!confirmed) return;

      const previousMessages = messages;

      try {
        setDeletingId(messageId);

        setMessages((prev) =>
          prev.filter((message) => message.id !== messageId),
        );

        await deleteMessageContactForm(messageId);

        toast.success("Message supprimé.");
        await refreshUnreadCount();
      } catch (error) {
        console.error("Erreur suppression message :", error);
        setMessages(previousMessages);
        toast.error("Impossible de supprimer ce message.");
      } finally {
        setDeletingId(null);
      }
    },
    [messages, refreshUnreadCount],
  );

  const exportCSV = useCallback(() => {
    const csv = buildContactMessagesCSV(filteredMessages);
    downloadCSV("messages-contactform.csv", csv);
    toast.success("Export CSV généré.");
  }, [filteredMessages]);

  const resetFilters = useCallback(() => {
    setQ("");
    setReadFilter("all");
    setDateFrom("");
    setDateTo("");
    setSort("date_desc");
    setPage(1);
  }, []);

  return {
    messages,
    filteredMessages,
    pageRows,
    stats,

    unreadCount,
    loading,
    deletingId,
    markingId,

    q,
    setQ,
    readFilter,
    setReadFilter,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    sort,
    setSort,

    page,
    setPage,
    pageSize,
    setPageSize,
    pageCount,

    loadMessages,
    toggleReadStatus,
    markAllAsRead,
    deleteMessage,
    exportCSV,
    resetFilters,
  };
}
